import React, { useEffect, useState } from 'react';
import './LlmModelSettings.css';

const formatPrice = (value) => {
  if (value === null || value === undefined) return null;
  if (value === 0) return '$0';
  return `$${value < 0.01 ? value.toFixed(4) : value.toFixed(2)}`;
};

const LlmModelSettings = ({ apiBase, authHeaders, onUnauthorized }) => {
  const [tasks, setTasks] = useState([]);
  const [selections, setSelections] = useState({});
  // {provider: [{id, name, pricing}]} — OpenRouter 카탈로그 응답 후 채워짐
  const [optionsByProvider, setOptionsByProvider] = useState(null);
  const [optionsStatus, setOptionsStatus] = useState('OpenRouter 모델 목록을 불러오는 중...');
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const applyTasks = (nextTasks) => {
    setTasks(nextTasks);
    setSelections(Object.fromEntries(nextTasks.map((task) => [task.key, task.override || ''])));
  };

  const fetchTasks = async () => {
    setLoadingTasks(true);
    try {
      const response = await fetch(apiBase, { headers: authHeaders });
      if (response.status === 401) {
        onUnauthorized();
        return;
      }
      const body = await response.json();
      if (!response.ok || !body.success) throw new Error(body.error || 'load failed');
      applyTasks(body.data.tasks || []);
    } catch (e) {
      setMessage({ type: 'error', text: 'LLM 모델 설정을 불러오지 못했습니다.' });
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${apiBase}/options`, { headers: authHeaders });
      if (response.status === 401) {
        onUnauthorized();
        return;
      }
      const body = await response.json();
      if (!response.ok || !body.success) throw new Error(body.error || 'fetch failed');
      const options = body.data.options || {};
      setOptionsByProvider(options);
      const count = Object.values(options).reduce((sum, list) => sum + (list || []).length, 0);
      setOptionsStatus(`OpenRouter 모델 ${count}개를 불러왔습니다.`);
    } catch (e) {
      setOptionsStatus('OpenRouter 모델 목록을 불러오지 못했습니다. 현재 저장값과 기본값만 선택할 수 있습니다.');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(apiBase, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ models: selections })
      });
      if (response.status === 401) {
        onUnauthorized();
        return;
      }
      const body = await response.json();
      if (!response.ok || !body.success) {
        setMessage({ type: 'error', text: body.error || '저장에 실패했습니다.' });
        return;
      }
      applyTasks(body.data.tasks || []);
      setMessage({ type: 'success', text: body.message || 'LLM 모델 설정을 저장했습니다.' });
    } catch (e) {
      setMessage({ type: 'error', text: '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const findPricing = (task, modelId) => {
    const list = (optionsByProvider || {})[task.provider] || [];
    const model = list.find((option) => option.id === modelId);
    return model ? model.pricing : null;
  };

  const renderCost = (task) => {
    const effective = selections[task.key] || task.default;
    const pricing = findPricing(task, effective);
    const inPrice = pricing ? formatPrice(pricing.in) : null;
    const outPrice = pricing ? formatPrice(pricing.out) : null;
    if (inPrice === null && outPrice === null) return <span className="llm-muted">&ndash;</span>;
    return (
      <>
        <div>입력 {inPrice || '-'}</div>
        <div>출력 {outPrice || '-'}</div>
      </>
    );
  };

  const renderModelSelect = (task) => {
    const current = selections[task.key] || '';
    const list = (optionsByProvider || {})[task.provider] || [];
    // 저장된 모델이 실시간 목록에서 사라져도 선택을 유지해 저장 시 조용히 초기화되지 않게 함
    const isStale = current && !list.some((option) => option.id === current);

    return (
      <select
        className="llm-model-select"
        value={current}
        onChange={(e) => setSelections({ ...selections, [task.key]: e.target.value })}
      >
        <option value="">기본값 사용 ({task.default})</option>
        {list.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name === option.id ? option.id : `${option.id} — ${option.name}`}
          </option>
        ))}
        {isStale && (
          <option value={current}>
            {current} ({optionsByProvider ? '목록에 없음 · ' : ''}현재 저장값)
          </option>
        )}
      </select>
    );
  };

  return (
    <div className="sessions-section llm-models-section">
      <div className="section-header llm-page-header">
        <div>
          <h3>🤖 LLM 모델 관리</h3>
          <p className="llm-muted">
            각 단계에서 사용하는 LLM 모델을 변경합니다. 모델 목록과 토큰 비용은 OpenRouter에서
            실시간으로 가져옵니다. "기본값 사용"을 선택하면 .env 에 설정된 기본 모델이 적용됩니다.
          </p>
          <p className="llm-muted" role="status" aria-live="polite">{optionsStatus}</p>
        </div>
        <button onClick={handleSave} disabled={saving || loadingTasks} className="apply-btn llm-save-btn">
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>

      {message.text && (
        <div className={message.type === 'error' ? 'error-message' : 'llm-success-message'}>
          {message.text}
        </div>
      )}

      <div className="table-container">
        <table className="sessions-table llm-table">
          <thead>
            <tr>
              <th>기능</th>
              <th>현재 설정 모델</th>
              <th>모델 선택</th>
              <th>토큰 비용 ($/1M)</th>
            </tr>
          </thead>
          <tbody>
            {loadingTasks && tasks.length === 0 && (
              <tr>
                <td colSpan={4}>불러오는 중...</td>
              </tr>
            )}
            {tasks.map((task) => (
              <tr key={task.key}>
                <td className="llm-func-cell">
                  <span className="llm-func-label">{task.label}</span>
                  <div className="llm-muted llm-func-desc">{task.description}</div>
                </td>
                <td className="llm-current-cell">
                  <code>{task.current}</code>
                  {task.override ? (
                    <div className="llm-changed">
                      변경됨{' '}
                      <button
                        type="button"
                        className="llm-reset-link"
                        onClick={() => setSelections({ ...selections, [task.key]: '' })}
                      >
                        기본값으로
                      </button>
                    </div>
                  ) : (
                    <div className="llm-muted llm-changed">기본값 ({task.env_var})</div>
                  )}
                </td>
                <td>{renderModelSelect(task)}</td>
                <td className="llm-cost-cell">{renderCost(task)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LlmModelSettings;
